import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faScissors } from '@fortawesome/free-solid-svg-icons';
import { useSelector } from 'react-redux';
import { useState, useEffect, useCallback } from 'react';
import DownloadButton from './DownloadButton';
import ShareButton from './ShareButton';
import VideoFeedbackButton from './VideoFeedbackButton';
import styles from './styles.module.css';
import BBBResourceButton from '../BBBResourcesLayouts/BBBResourceButton';
import { getCode } from '../../../store/params/selectors';
import { RecordItem } from '../BBBResourcesLayouts/types';
import { RootState } from '../../../store';

interface Props {
  show: boolean;
  clipModeActive: boolean;
  open: () => void;
  bbbResourcesLength: number;
  bbbResources: RecordItem[];
}

const SideButtons = ({
  show,
  clipModeActive,
  open,
  bbbResourcesLength,
  bbbResources,
}: Props): JSX.Element => {
  const [showBBBResourceButton, setShowBBBResourceButton] = useState(false);
  const [translationType, setTranslationType] = useState<string | undefined>(undefined);
  const [buttonTimeout, setButtonTimeout] = useState<NodeJS.Timeout | null>(null);

  const container = `${styles.container} ${clipModeActive && styles.containerClipModeActive} ${
    !show && styles.containerHide
  }`;

  const clipIconClass = `${styles.clipIcon} ${clipModeActive && styles.clipIconShow}`;

  const code = useSelector(getCode);
  const { showBBBResourceButton: showBBBResourceButtonSetting } = useSelector(
    (state: RootState) => state.settings
  );

  // Function to get current video time
  const getCurrentVideoTime = useCallback((): number => {
    const mainVideoPlayer = document.querySelector(
      '#mainVideoPlayerContainer video'
    ) as HTMLVideoElement;
    if (mainVideoPlayer) {
      return mainVideoPlayer.currentTime;
    }
    return 0;
  }, []);

  // Function to check if current time is within any BBB resource time range
  const isCurrentTimeInBBBResourceRange = useCallback((): boolean => {
    if (!bbbResources || bbbResources.length === 0) return false;

    const currentTime = getCurrentVideoTime();

    return bbbResources.some(
      resource =>
        resource.startTime > 0 &&
        resource.endTime > 0 &&
        currentTime >= resource.startTime &&
        currentTime <= resource.endTime
    );
  }, [bbbResources, getCurrentVideoTime]);

  // Function to handle BBB resource button click
  const handleBBBResourceClick = () => {
    // Clear any existing timeout
    if (buttonTimeout) {
      clearTimeout(buttonTimeout);
      setButtonTimeout(null);
    }
    open();
  };

  useEffect(() => {
    let detectedType: string | undefined;
    const lowerCode = code?.toLowerCase();

    if (/bbb|aslv|islv/i.test(lowerCode || '')) {
      detectedType = 'bbb';
    } else if (lowerCode?.includes('cbt')) {
      detectedType = 'cbt';
    }

    setTranslationType(detectedType);
  }, [code]);

  // Update current video time and check if button should be shown
  useEffect(() => {
    const updateTimeAndButton = () => {
      const shouldShow =
        translationType === 'bbb' &&
        isCurrentTimeInBBBResourceRange() &&
        showBBBResourceButtonSetting;

      if (shouldShow && !showBBBResourceButton) {
        // Show the button when entering a resource time range
        setShowBBBResourceButton(true);

        // Set a timeout to start fade out after 5 seconds if not clicked
        const timeout = setTimeout(() => {
          setShowBBBResourceButton(false);
          setButtonTimeout(null);
        }, 5000);

        setButtonTimeout(timeout);
      } else if (!shouldShow && showBBBResourceButton) {
        // Start fade out when exiting a resource time range
        setShowBBBResourceButton(false);
        if (buttonTimeout) {
          clearTimeout(buttonTimeout);
          setButtonTimeout(null);
        }
      }
    };

    // Update immediately
    updateTimeAndButton();

    // Set up interval to check more frequently for better responsiveness
    const interval = setInterval(updateTimeAndButton, 500);

    return () => {
      clearInterval(interval);
      if (buttonTimeout) {
        clearTimeout(buttonTimeout);
      }
    };
  }, [
    translationType,
    isCurrentTimeInBBBResourceRange,
    showBBBResourceButton,
    buttonTimeout,
    showBBBResourceButtonSetting,
  ]);

  return (
    <>
      {/* Main controls container - show when controls are visible */}
      {show && (
        <div className={container}>
          {showBBBResourceButton && bbbResourcesLength > 0 && (
            <BBBResourceButton onClick={handleBBBResourceClick} />
          )}
          <VideoFeedbackButton />
          <ShareButton />
          <DownloadButton />
          <FontAwesomeIcon className={clipIconClass} icon={faScissors} />
        </div>
      )}
      {/* Only BBB resource button when controls are hidden and start time matches */}
      {!show && showBBBResourceButton && bbbResourcesLength > 0 && (
        <div className={`${styles.bbbResourceButtonContainer}`}>
          <BBBResourceButton onClick={handleBBBResourceClick} />
        </div>
      )}
    </>
  );
};

export default SideButtons;
